import {
  Bot,
  Download,
  Image as ImageIcon,
  Loader2,
  Palette,
  RefreshCw,
  Sparkles,
  Type,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import postsService from "../../services/postsService";
import {
  IMAGE_PROVIDER_OPTIONS,
  imageProviderHint,
} from "../../shared/imageProviders";
import { cn } from "../../utils/cn";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Modal from "../ui/Modal";
import Select from "../ui/Select";
import Textarea from "../ui/Textarea";

export const FORMAT_OPTIONS = [
  { value: "instagram_feed", label: "Instagram Feed (1080×1080)" },
  { value: "instagram_feed_4_5", label: "Instagram Feed 4:5 (1080×1350)" },
  { value: "instagram_stories", label: "Instagram Stories (1080×1920)" },
  { value: "instagram_reels", label: "Instagram Reels (1080×1920)" },
  { value: "facebook_post", label: "Facebook Post (1200×628)" },
  { value: "linkedin_post", label: "LinkedIn Post (1200×627)" },
  { value: "twitter_post", label: "Twitter/X Post (1200×675)" },
];

const STYLE_OPTIONS = [
  { value: "modern", label: "Moderno — escuro com laranja" },
  { value: "minimal", label: "Minimalista — claro e limpo" },
  { value: "vibrant", label: "Vibrante — roxo com amarelo/verde" },
  { value: "elegant", label: "Elegante — preto com dourado" },
];

interface Suggestions {
  headers: string[];
  bodies: string[];
  ctas: string[];
}

interface CampaignCreativeEditorProps {
  open: boolean;
  onClose: () => void;
  postId: number | null;
  initialHeader?: string;
  initialBody?: string;
  initialCta?: string;
  /** URL do fundo cru (sem texto) da última geração deste post, se houver —
   * permite editar só o texto sem pagar/esperar por uma imagem de IA nova. */
  initialRawBackgroundUrl?: string | null;
  onCreativeGenerated?: (imageInfo: any) => void;
}

// Chips clicáveis de sugestão
const SuggestionChips: React.FC<{
  items: string[];
  onPick: (v: string) => void;
}> = ({ items, onPick }) => (
  <div className="flex flex-wrap gap-1.5 mt-1">
    {items.map((item, i) => (
      <button
        key={i}
        type="button"
        onClick={() => onPick(item)}
        className="text-[11px] px-2.5 py-1 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700
                   hover:bg-indigo-100 hover:border-indigo-400 dark:border-indigo-700 dark:bg-indigo-900/30
                   dark:text-indigo-300 dark:hover:bg-indigo-800/50 transition-colors cursor-pointer"
      >
        {item}
      </button>
    ))}
  </div>
);

const CampaignCreativeEditor: React.FC<CampaignCreativeEditorProps> = ({
  open,
  onClose,
  postId,
  initialHeader = "",
  initialBody = "",
  initialCta = "",
  initialRawBackgroundUrl = null,
  onCreativeGenerated,
}) => {
  const { t } = useTranslation();
  const [header, setHeader] = useState(initialHeader);
  const [body, setBody] = useState(initialBody);
  const [cta, setCta] = useState(initialCta);
  const [format, setFormat] = useState("instagram_feed");
  const [style, setStyle] = useState("modern");
  const [bgPrompt, setBgPrompt] = useState("");
  const [provider, setProvider] = useState("nano-banana");
  const [rawBackgroundUrl, setRawBackgroundUrl] = useState<string | null>(
    initialRawBackgroundUrl,
  );
  const [reuseBackground, setReuseBackground] = useState(
    Boolean(initialRawBackgroundUrl),
  );

  const [loading, setLoading] = useState(false);
  const [generationStatus, setGenerationStatus] = useState<
    "idle" | "pending" | "processing" | "completed" | "failed"
  >("idle");
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState<string | null>(null);
  const [visionReviewIssues, setVisionReviewIssues] = useState<string[] | null>(
    null,
  );

  const [loadingHooks, setLoadingHooks] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestions | null>(null);
  const [hooksError, setHooksError] = useState<string | null>(null);
  const [carouselCount, setCarouselCount] = useState(1);

  useEffect(() => {
    if (open) {
      setHeader(initialHeader);
      setBody(initialBody);
      setCta(initialCta);
      setFormat("instagram_feed");
      setStyle("modern");
      setBgPrompt("");
      setProvider("nano-banana");
      setLoading(false);
      setGenerationStatus("idle");
      setError(null);
      setPreviewUrl(null);
      setDimensions(null);
      setSuggestions(null);
      setHooksError(null);
      setCarouselCount(1);
      setRawBackgroundUrl(initialRawBackgroundUrl);
      setReuseBackground(Boolean(initialRawBackgroundUrl));
    }
  }, [open, initialHeader, initialBody, initialCta, initialRawBackgroundUrl]);

  // Ao abrir o editor pra um post JÁ EXISTENTE, busca automaticamente hooks reais
  // via IA (mesmo agente do botão "Sugerir hooks") pra pré-preencher com um gancho
  // baseado no contexto real do post, em vez de depender só da divisão de
  // parágrafos usada como valor inicial (initialHeader/Body/Cta). Só sobrescreve
  // os campos se o usuário ainda não tiver editado nada nesse meio-tempo.
  useEffect(() => {
    if (!open || !postId) return;
    let cancelled = false;
    (async () => {
      setLoadingHooks(true);
      try {
        const result = await postsService.suggestHooks(postId, format, style);
        if (cancelled || !result?.success) return;
        setSuggestions(result.suggestions);
        setHeader((prev) =>
          prev === initialHeader && result.suggestions.headers?.[0]
            ? result.suggestions.headers[0]
            : prev,
        );
        setBody((prev) =>
          prev === initialBody && result.suggestions.bodies?.[0]
            ? result.suggestions.bodies[0]
            : prev,
        );
        setCta((prev) =>
          prev === initialCta && result.suggestions.ctas?.[0]
            ? result.suggestions.ctas[0]
            : prev,
        );
      } catch {
        // Silencioso: os valores iniciais (deriveCreativeEditorDefaults) já
        // ficam nos campos como fallback razoável.
      } finally {
        if (!cancelled) setLoadingHooks(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, postId]);

  const handleSuggestHooks = async () => {
    if (!postId) return;
    setLoadingHooks(true);
    setHooksError(null);
    setSuggestions(null);
    try {
      const result = await postsService.suggestHooks(postId, format, style);
      if (!result?.success)
        throw new Error(result?.error || t("Erro ao sugerir hooks"));
      setSuggestions(result.suggestions);
    } catch (err: any) {
      setHooksError(err?.message || t("Erro ao carregar sugestões."));
    } finally {
      setLoadingHooks(false);
    }
  };

  const handleGenerate = async () => {
    if (!postId) return;
    if (!header.trim() && !body.trim()) {
      setError(t("Preencha pelo menos o Título ou o Corpo do criativo."));
      return;
    }
    setLoading(true);
    setGenerationStatus("pending");
    setError(null);
    setVisionReviewIssues(null);
    try {
      const result = await postsService.generateCreativeAndWait(
        postId,
        {
          header: header.trim(),
          body: body.trim(),
          cta: cta.trim(),
          format,
          template_style: style,
          background_prompt: bgPrompt.trim() || undefined,
          provider,
          carousel_count: carouselCount > 1 ? carouselCount : undefined,
          reuse_background_url:
            reuseBackground && rawBackgroundUrl ? rawBackgroundUrl : undefined,
        },
        {
          onStatus: (status) => {
            if (status === "completed") {
              setGenerationStatus("completed");
            } else if (status === "error" || status === "failed") {
              setGenerationStatus("failed");
            } else if (status === "processing" || status === "running") {
              setGenerationStatus("processing");
            } else {
              setGenerationStatus("pending");
            }
          },
        },
      );

      if (!result?.success) {
        throw new Error(
          (result?.error as string) ||
            (result?.message as string) ||
            t("Erro ao gerar criativo"),
        );
      }

      const imageInfo = result.image_info as
        | Record<string, unknown>
        | undefined;
      const url =
        (imageInfo?.public_url as string) ||
        (imageInfo?.variants as { original?: string })?.original ||
        (imageInfo?.filename
          ? `/api/images/${imageInfo.filename as string}`
          : null);

      setPreviewUrl(url);
      const newRawBackgroundUrl = imageInfo?.raw_background_url as
        | string
        | undefined;
      if (newRawBackgroundUrl) {
        setRawBackgroundUrl(newRawBackgroundUrl);
        setReuseBackground(true);
      }
      const carouselImages = result.carousel_images as unknown[] | undefined;
      setDimensions(
        result.is_carousel && carouselImages?.length
          ? `${(result.dimensions as string) || ""} · ${t("carrossel de {{n}} imagens").replace("{{n}}", String(carouselImages.length))}`
          : (result.dimensions as string) || null,
      );
      const visionReview = result.vision_review as
        | { overall_pass?: boolean; issues?: string[] }
        | undefined;
      setVisionReviewIssues(
        visionReview && visionReview.overall_pass === false
          ? visionReview.issues || []
          : null,
      );

      setGenerationStatus("completed");
      onCreativeGenerated?.(imageInfo);
    } catch (err: any) {
      setGenerationStatus("failed");
      setError(err?.message || t("Erro desconhecido ao gerar criativo."));
    } finally {
      setLoading(false);
    }
  };

  const loadingMessage =
    generationStatus === "processing"
      ? t("Compondo textos sobre o fundo…")
      : t("Gerando fundo com Gemini…");

  const handleDownload = () => {
    if (!previewUrl) return;
    const a = document.createElement("a");
    a.href = previewUrl;
    a.download = `criativo_${format}_${Date.now()}.jpg`;
    a.click();
  };

  const isPortrait = format.includes("stories") || format.includes("reels");

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("Editor de Criativo de Campanha")}
      maxWidth="2xl"
    >
      <div className="flex flex-col gap-5">
        {/* Agent hook button */}
        <div className="flex items-center justify-between rounded-xl border border-indigo-100 bg-indigo-50/60 dark:border-indigo-800 dark:bg-indigo-900/20 px-4 py-3">
          <div className="flex items-center gap-2">
            <Bot size={16} className="text-indigo-500" />
            <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
              {t("Agente de Hooks")}
            </span>
            <span className="text-xs text-indigo-500 dark:text-indigo-400">
              —{" "}
              {t(
                "frases de impacto geradas por IA com base no conteúdo do post",
              )}
            </span>
          </div>
          <button
            type="button"
            onClick={handleSuggestHooks}
            disabled={loadingHooks || !postId}
            className={cn(
              "flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors",
              "bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed",
            )}
          >
            {loadingHooks ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Sparkles size={12} />
            )}
            {loadingHooks ? t("Gerando…") : t("Sugerir hooks")}
          </button>
        </div>

        {hooksError && (
          <p className="text-xs text-red-500 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
            {hooksError}
          </p>
        )}

        {/* Two-column layout */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left: form */}
          <div className="flex-1 flex flex-col gap-4 min-w-0">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
              <Type size={15} />
              {t("Textos do criativo")}
            </div>

            {/* Header field + suggestions */}
            <div className="flex flex-col gap-1">
              <Input
                label={t("Título (Header)")}
                placeholder={t("Ex: Transforme seu negócio com IA")}
                value={header}
                onChange={(e) => setHeader(e.target.value)}
              />
              {suggestions?.headers && suggestions.headers.length > 0 && (
                <SuggestionChips
                  items={suggestions.headers}
                  onPick={setHeader}
                />
              )}
            </div>

            {/* Body field + suggestions */}
            <div className="flex flex-col gap-1">
              <Textarea
                label={t("Corpo (Body)")}
                placeholder={t(
                  "Ex: Automatize postagens, analise resultados e cresça mais rápido.",
                )}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={3}
              />
              {suggestions?.bodies && suggestions.bodies.length > 0 && (
                <SuggestionChips items={suggestions.bodies} onPick={setBody} />
              )}
            </div>

            {/* CTA field + suggestions */}
            <div className="flex flex-col gap-1">
              <Input
                label={t("Call to Action (CTA)")}
                placeholder={t("Ex: Comece Grátis")}
                value={cta}
                onChange={(e) => setCta(e.target.value)}
              />
              {suggestions?.ctas && suggestions.ctas.length > 0 && (
                <SuggestionChips items={suggestions.ctas} onPick={setCta} />
              )}
            </div>

            {/* Format + Style */}
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mt-1">
              <Palette size={15} />
              {t("Formato e estilo")}
            </div>

            {rawBackgroundUrl && (
              <label className="flex items-start gap-2.5 rounded-xl border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20 px-3 py-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={reuseBackground}
                  onChange={(e) => setReuseBackground(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded-sm border-green-300 text-green-600 focus:ring-green-500"
                />
                <span className="text-xs text-green-800 dark:text-green-300">
                  <span className="font-semibold">
                    {t("Reaproveitar fundo atual")}
                  </span>{" "}
                  {t(
                    "— edita só o texto sobre a mesma imagem, sem gastar crédito nem esperar uma imagem nova. Desmarque pra gerar um fundo diferente.",
                  )}
                </span>
              </label>
            )}

            <Select
              label={t("Formato")}
              options={FORMAT_OPTIONS}
              value={format}
              onChange={setFormat}
            />

            <div className="flex flex-col gap-1">
              <Select
                label={t("Carrossel")}
                options={[1, 2, 3, 4, 5, 6].map((n) => ({
                  value: String(n),
                  label:
                    n === 1
                      ? t("Não — apenas 1 imagem (padrão)")
                      : t("{{n}} imagens (hooks diferentes)").replace(
                          "{{n}}",
                          String(n),
                        ),
                }))}
                value={String(carouselCount)}
                onChange={(v) => setCarouselCount(Number(v))}
              />
              {carouselCount > 1 && (
                <p className="text-xs text-gray-500">
                  {t(
                    "Gera 1 fundo e recompõe o texto acima em cada peça; as demais usam hooks alternativos sugeridos pela IA com base no mesmo post.",
                  )}
                </p>
              )}
            </div>

            <Select
              label={t("Estilo visual")}
              options={STYLE_OPTIONS.map((o) => ({
                ...o,
                label: t(o.label),
              }))}
              value={style}
              onChange={setStyle}
            />

            {!(reuseBackground && rawBackgroundUrl) && (
              <>
                <Select
                  label={t("Provedor de fundo (IA)")}
                  options={[...IMAGE_PROVIDER_OPTIONS]}
                  value={provider}
                  onChange={setProvider}
                />
                <p className="text-xs text-gray-500">
                  {t(imageProviderHint(provider))}
                </p>

                {/* Background prompt */}
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mt-1">
                  <ImageIcon size={15} />
                  {t("Background (opcional)")}
                </div>

                <Textarea
                  label={t("Descrição do fundo (opcional)")}
                  placeholder={t(
                    "Ex: escritório moderno, pessoas trabalhando, tons azuis e brancos",
                  )}
                  value={bgPrompt}
                  onChange={(e) => setBgPrompt(e.target.value)}
                  rows={2}
                  hint={t(
                    "Se vazio, a IA usará o título/conteúdo do post para gerar o fundo.",
                  )}
                />
              </>
            )}

            {error && (
              <p className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
                {error}
              </p>
            )}
          </div>

          {/* Right: preview */}
          <div className="shrink-0 flex flex-col items-center gap-3 lg:w-60">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 self-start">
              {t("Pré-visualização")}
            </p>
            <div
              className={cn(
                "w-full rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 overflow-hidden flex items-center justify-center",
                isPortrait ? "aspect-9/16" : "aspect-square",
              )}
            >
              {loading ? (
                <div className="flex flex-col items-center gap-3 text-gray-400">
                  <Loader2 size={32} className="animate-spin text-violet-500" />
                  <span className="text-xs text-center px-3">
                    {loadingMessage}
                  </span>
                </div>
              ) : previewUrl ? (
                <img
                  src={previewUrl}
                  alt={t("Criativo gerado")}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="flex flex-col items-center gap-2 text-gray-400 px-4 text-center">
                  <Sparkles size={28} />
                  <span className="text-xs">
                    {t('Clique em "Gerar Criativo" para ver o resultado')}
                  </span>
                </div>
              )}
            </div>

            {dimensions && (
              <span className="text-xs text-gray-400">{dimensions} px</span>
            )}

            {visionReviewIssues && (
              <div className="w-full text-xs text-amber-700 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2 space-y-1">
                <p className="font-semibold">
                  {t("IA detectou possíveis problemas no criativo:")}
                </p>
                <ul className="list-disc list-inside space-y-0.5">
                  {visionReviewIssues.slice(0, 3).map((issue, i) => (
                    <li key={i}>{issue}</li>
                  ))}
                </ul>
              </div>
            )}

            {previewUrl && (
              <button
                onClick={handleDownload}
                className="flex items-center gap-1.5 text-xs text-violet-600 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300 underline"
              >
                <Download size={12} />
                {t("Baixar imagem")}
              </button>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {t("Fechar")}
          </Button>

          <div className="flex items-center gap-3">
            {previewUrl && (
              <Button
                variant="outline"
                onClick={handleGenerate}
                disabled={loading}
                className="flex items-center gap-1.5"
              >
                <RefreshCw size={14} />
                {t("Regenerar")}
              </Button>
            )}
            <Button
              onClick={handleGenerate}
              disabled={loading || !postId}
              className="flex items-center gap-2"
            >
              {loading ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <Sparkles size={15} />
              )}
              {loading ? t("Gerando…") : t("Gerar Criativo")}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CampaignCreativeEditor;
